import { DragEvent, FC, MouseEvent, memo, useEffect, useState } from 'react';
import { useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CalendarTable, CalendarCell } from '@/components/ui';
import { CalendarDay, CalendarData, Tag } from '../types';
import { useToast } from '@/contexts/ToastContext';
import { getErrorMessage } from '@/utils/error';

interface PublishingCalendarTabProps {
  calendar: CalendarData;
  loading?: boolean;
  updatingEntryId?: number | null;
  error?: string | null;
  currentMonth: string;
  onMonthChange: (month: string) => void;
  onUpdateCalendarEntry?: (id: number, updateData: { plannedDate?: string; dayNumber?: number; status?: string }) => Promise<any>;
  onRefetchCalendar?: () => Promise<void>;
}

// Constants
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
] as const;

const MAX_VISIBLE_TAGS = 3;

const TAG_COLORS: Record<string, string> = {
  news: 'bg-blue-100 text-blue-700 border border-blue-300',
  company: 'bg-purple-100 text-purple-700 border border-purple-300',
  product: 'bg-green-100 text-green-700 border border-green-300',
  event: 'bg-orange-100 text-orange-700 border border-orange-300',
};

const DEFAULT_TAG_COLOR = 'bg-gray-100 text-gray-700 border border-gray-300';

// Utility functions
const getTagColor = (type: string): string => {
  return TAG_COLORS[type] || DEFAULT_TAG_COLOR;
};

const parseMonthLabel = (monthLabel: string): { monthName: string; year: number; monthIndex: number } | null => {
  const [monthName, yearStr] = monthLabel.split(' ');
  const year = parseInt(yearStr);
  const monthIndex = MONTH_NAMES.indexOf(monthName as typeof MONTH_NAMES[number]);

  if (monthIndex === -1 || isNaN(year)) {
    return null;
  }

  return { monthName, year, monthIndex };
};

const formatDateString = (year: number, monthIndex: number, day: number): string => {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const normalizeDateString = (date: string | Date): string => {
  if (typeof date === 'string') {
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return date;
    }
    return date.split('T')[0];
  }
  return new Date(date).toISOString().split('T')[0];
};

// Memoized Legend Component
const CalendarLegend = memo(() => (
  <div className="mt-6 flex flex-wrap gap-4 text-xs">
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 bg-blue-100 border-2 border-blue-300 rounded"></div>
      <span className="text-gray-600">News</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 bg-purple-100 border-2 border-purple-300 rounded"></div>
      <span className="text-gray-600">Company</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 bg-green-100 border-2 border-green-300 rounded"></div>
      <span className="text-gray-600">Product</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 bg-orange-100 border-2 border-orange-300 rounded"></div>
      <span className="text-gray-600">Event</span>
    </div>
  </div>
));
CalendarLegend.displayName = 'CalendarLegend';

/**
 * Marketing-specific calendar view
 * Uses pure CalendarTable component with marketing data
 */
export const PublishingCalendarTab: FC<PublishingCalendarTabProps> = ({
  calendar,
  loading = false,
  updatingEntryId = null,
  error = null,
  currentMonth,
  onMonthChange,
  onUpdateCalendarEntry,
}) => {
  const { addToast } = useToast();
  const [draggedTag, setDraggedTag] = useState<Tag | null>(null);
  const [dragOverCell, setDragOverCell] = useState<{ weekIndex: number; cellIndex: number } | null>(null);
  // Optimistic calendar state - overrides the prop calendar when there's an optimistic update
  const [optimisticCalendar, setOptimisticCalendar] = useState<CalendarData | null>(null);
  // Expanded cell state - tracks which cell is expanded to show all posts
  const [expandedCell, setExpandedCell] = useState<{ weekIndex: number; cellIndex: number } | null>(null);

  // Use optimistic calendar if available, otherwise use prop calendar
  const displayCalendar = optimisticCalendar || calendar;

  // Clear optimistic calendar when prop calendar changes (after successful refetch)
  useEffect(() => {
    if (optimisticCalendar && !updatingEntryId) {
      setOptimisticCalendar(null);
    }
  }, [calendar, updatingEntryId, optimisticCalendar]);

  // Apply optimistic update to calendar - move post from old date to new date
  const applyOptimisticUpdate = useCallback((
    currentCalendar: CalendarData,
    tag: Tag,
    newDateString: string,
    oldDateString: string | null
  ): CalendarData => {
    const monthInfo = parseMonthLabel(currentCalendar.monthLabel);
    if (!monthInfo) {
      return currentCalendar; // Invalid month, return unchanged
    }

    const { year, monthIndex } = monthInfo;

    // Deep clone the calendar
    const updatedCalendar: CalendarData = {
      monthLabel: currentCalendar.monthLabel,
      weeks: currentCalendar.weeks.map(week =>
        week.map(day => ({
          ...day,
          tags: day.tags ? [...day.tags] : undefined,
        }))
      ),
    };

    // Find and remove the tag from the old date
    if (oldDateString) {
      const [oldYear, oldMonth, oldDay] = oldDateString.split('-').map(Number);

      // Only remove if it's in the current month
      if (oldYear === year && oldMonth === monthIndex + 1) {
        const firstDay = new Date(year, monthIndex, 1);
        const startDay = firstDay.getDay();
        const dayIndex = startDay + oldDay - 1;
        const weekIndex = Math.floor(dayIndex / 7);
        const cellIndex = dayIndex % 7;

        if (updatedCalendar.weeks[weekIndex] && updatedCalendar.weeks[weekIndex][cellIndex]) {
          const day = updatedCalendar.weeks[weekIndex][cellIndex];
          if (day.tags) {
            day.tags = day.tags.filter(t => t.calendarEntryId !== tag.calendarEntryId);
            if (day.tags.length === 0) {
              day.tags = undefined;
            }
          }
        }
      }
    }

    // Add the tag to the new date
    const [newYear, newMonth, newDay] = newDateString.split('-').map(Number);

    // Only add if it's in the current month
    if (newYear === year && newMonth === monthIndex + 1) {
      const firstDay = new Date(year, monthIndex, 1);
      const startDay = firstDay.getDay();
      const dayIndex = startDay + newDay - 1;
      const weekIndex = Math.floor(dayIndex / 7);
      const cellIndex = dayIndex % 7;

      if (updatedCalendar.weeks[weekIndex] && updatedCalendar.weeks[weekIndex][cellIndex]) {
        const day = updatedCalendar.weeks[weekIndex][cellIndex];
        if (!day.tags) {
          day.tags = [];
        }
        // Create updated tag with new date in calendarEntry
        const updatedTag: Tag = {
          ...tag,
          calendarEntry: tag.calendarEntry ? {
            ...tag.calendarEntry,
            plannedDate: newDateString,
          } : undefined,
        };
        day.tags.push(updatedTag);
      }
    }

    return updatedCalendar;
  }, []);

  const handleDragStart = useCallback((e: DragEvent<HTMLDivElement>, tag: Tag) => {
    setDraggedTag(tag);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify(tag));
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '0.5';
    }
  }, []);

  const handleDragEnd = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedTag(null);
    setDragOverCell(null);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>, weekIndex: number, cellIndex: number, day: number | null) => {
    e.preventDefault();
    if (day !== null) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverCell({ weekIndex, cellIndex });
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  }, []);

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const { clientX: x, clientY: y } = e;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverCell(null);
    }
  }, []);

  const handleDrop = useCallback(async (
    e: DragEvent<HTMLDivElement>,
    _weekIndex: number,
    _cellIndex: number,
    day: number | null
  ) => {
    e.preventDefault();
    setDragOverCell(null);

    if (!draggedTag || day === null || !draggedTag.calendarEntryId) {
      return;
    }

    const monthInfo = parseMonthLabel(currentMonth);
    if (!monthInfo) {
      console.error('Invalid month:', currentMonth);
      return;
    }

    const { year, monthIndex } = monthInfo;
    const newDateString = formatDateString(year, monthIndex, day);

    // Normalize original date
    const originalDateString = draggedTag.calendarEntry?.plannedDate
      ? normalizeDateString(draggedTag.calendarEntry.plannedDate)
      : null;

    // Skip if same date
    if (originalDateString === newDateString) {
      setDraggedTag(null);
      return;
    }

    // Apply optimistic update
    const optimisticUpdate = applyOptimisticUpdate(
      displayCalendar,
      draggedTag,
      newDateString,
      originalDateString
    );
    setOptimisticCalendar(optimisticUpdate);
    setDraggedTag(null);

    // Update backend
    if (onUpdateCalendarEntry && draggedTag.calendarEntryId) {
      try {
        await onUpdateCalendarEntry(draggedTag.calendarEntryId, {
          plannedDate: newDateString,
        });
      } catch (error: unknown) {
        console.error('Failed to update calendar entry:', error);
        setOptimisticCalendar(null); // Revert optimistic update
        addToast(getErrorMessage(error), 'error');
      }
    }
  }, [draggedTag, currentMonth, displayCalendar, applyOptimisticUpdate, onUpdateCalendarEntry]);

  const handleToggleExpand = useCallback((weekIndex: number, cellIndex: number) => {
    setExpandedCell(prev =>
      prev?.weekIndex === weekIndex && prev?.cellIndex === cellIndex
        ? null
        : { weekIndex, cellIndex }
    );
  }, []);

  const renderTags = useCallback((tags: Tag[], _day: number | null, weekIndex: number, cellIndex: number) => {
    const isExpanded = expandedCell?.weekIndex === weekIndex && expandedCell?.cellIndex === cellIndex;
    const hasMore = tags.length > MAX_VISIBLE_TAGS;
    const visibleTags = isExpanded ? tags : (hasMore ? tags.slice(0, MAX_VISIBLE_TAGS) : tags);
    const remainingCount = tags.length - MAX_VISIBLE_TAGS;

    const handleToggle = (e: MouseEvent) => {
      e.stopPropagation();
      handleToggleExpand(weekIndex, cellIndex);
    };

    return (
      <div className="space-y-1">
        <div className={`space-y-1 ${isExpanded ? 'max-h-[300px] overflow-y-auto' : 'max-h-[120px] overflow-y-auto'}`}>
          {visibleTags.map((tag, i) => {
            const isUpdating = updatingEntryId !== null && tag.calendarEntryId === updatingEntryId;
            return (
              <div
                key={`${tag.calendarEntryId || tag.postId || i}`}
                draggable={!isUpdating}
                onDragStart={(e) => handleDragStart(e, tag)}
                onDragEnd={handleDragEnd}
                className={`text-xs px-2 py-1 rounded cursor-move ${getTagColor(tag.type)} hover:shadow-md transition-all relative flex-shrink-0 ${isUpdating ? 'opacity-60 cursor-wait' : ''
                  }`}
                title={tag.label}
              >
                {isUpdating && (
                  <span className="absolute top-0 right-0 -mt-1 -mr-1">
                    <svg className="animate-spin h-3 w-3 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                )}
                <span className="truncate block">{tag.label}</span>
              </div>
            );
          })}
        </div>
        {hasMore && (
          <button
            type="button"
            onClick={handleToggle}
            className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-700 border border-gray-400 font-semibold text-center flex-shrink-0 w-full hover:bg-gray-300 transition-colors cursor-pointer"
            title={isExpanded ? `Click to collapse` : `Click to view all ${tags.length} posts`}
          >
            {isExpanded ? `Show less (${tags.length - MAX_VISIBLE_TAGS} hidden)` : `+${remainingCount} more`}
          </button>
        )}
      </div>
    );
  }, [expandedCell, updatingEntryId, handleDragStart, handleDragEnd, handleToggleExpand]);

  const handlePreviousMonth = useCallback(() => {
    const monthInfo = parseMonthLabel(currentMonth);
    if (!monthInfo) return;

    const { monthIndex, year } = monthInfo;
    const newMonthIndex = monthIndex > 0 ? monthIndex - 1 : 11;
    const newYear = monthIndex > 0 ? year : year - 1;

    onMonthChange(`${MONTH_NAMES[newMonthIndex]} ${newYear}`);
  }, [currentMonth, onMonthChange]);

  const handleNextMonth = useCallback(() => {
    const monthInfo = parseMonthLabel(currentMonth);
    if (!monthInfo) return;

    const { monthIndex, year } = monthInfo;
    const newMonthIndex = monthIndex < 11 ? monthIndex + 1 : 0;
    const newYear = monthIndex < 11 ? year : year + 1;

    onMonthChange(`${MONTH_NAMES[newMonthIndex]} ${newYear}`);
  }, [currentMonth, onMonthChange]);


  // Transform calendar data to CalendarCell format - memoized for performance
  const weeks = useMemo<CalendarCell[][]>(() =>
    displayCalendar.weeks.map((week, weekIndex) =>
      week.map((day: CalendarDay, cellIndex) => {
        const isDragOver = dragOverCell?.weekIndex === weekIndex && dragOverCell?.cellIndex === cellIndex;
        const isExpanded = expandedCell?.weekIndex === weekIndex && expandedCell?.cellIndex === cellIndex;

        return {
          day: day.day || undefined,
          isToday: day.today,
          content: day.tags && day.tags.length > 0 ? renderTags(day.tags, day.day, weekIndex, cellIndex) : undefined,
          onDrop: (e: DragEvent<HTMLDivElement>) => handleDrop(e, weekIndex, cellIndex, day.day),
          onDragOver: (e: DragEvent<HTMLDivElement>) => handleDragOver(e, weekIndex, cellIndex, day.day),
          onDragEnter: handleDragEnter,
          onDragLeave: handleDragLeave,
          className: isDragOver ? 'bg-blue-100 border-2 border-blue-400' : (isExpanded ? 'bg-gray-50' : ''),
        };
      })
    ),
    [displayCalendar.weeks, dragOverCell, expandedCell, renderTags, handleDrop, handleDragOver, handleDragEnter, handleDragLeave]
  );

  // Show loading only on initial load, not during updates
  if (loading && calendar.weeks.length === 0) {
    return (
      <section className="animate-fadeIn">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-gray-600">Loading calendar...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="animate-fadeIn">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-red-600">Error: {error}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="animate-fadeIn">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Publishing Calendar – {displayCalendar.monthLabel}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Approved documents scheduled for publishing
            </p>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousMonth}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
              aria-label="Next month"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        <CalendarTable weeks={weeks} />

        {/* Legend */}
        <CalendarLegend />
      </div>
    </section>
  );
};
